import React from "react";
import { ShieldCheck, Calendar, Clock, AlertCircle, Hash } from "lucide-react";
import { ConfigSection } from "../ui/ConfigSection";

/**
 * LicenseTab - Minimalist and lightweight view for software license information.
 */
export default function LicenseTab() {
    // Static data as requested
    const licenseData = {
        status: "Active",
        plan: "Enterprise Professional",
        validFrom: "May 01, 2025",
        validTo: "Nov 01, 2026",
        daysRemaining: 190,
        licenseKey: "VNS-SLR-XXXX-XXXX-8892"
    };

    return (
        <div className="max-w-4xl space-y-6 animate-in fade-in duration-500">
            
            <ConfigSection 
                title="System Subscription" 
                description="View your enterprise software license and validity period."
                dotColor="bg-emerald-500"
            >
                {/* Slim Full-Width Card */}
                <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm group">
                    
                    <div className="relative z-10">
                        {/* Header Section with Days Remaining */}
                        <div className="flex justify-between items-center mb-6 pb-6 border-b border-slate-100">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center">
                                    <ShieldCheck size={20} />
                                </div>
                                <div>
                                    <h3 className="text-[15px] font-semibold text-slate-800 tracking-tight leading-none mb-1">{licenseData.plan}</h3>
                                    <div className="flex items-center gap-2">
                                        <span className="flex items-center gap-1 text-emerald-600 font-medium text-[10px] uppercase tracking-wider bg-emerald-50/50 px-2 py-0.5 rounded-full border border-emerald-100/50">
                                            {licenseData.status} Subscription
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Slim Days Left Display */}
                            <div className="flex items-center gap-3 bg-slate-50 px-4 py-2 rounded-xl border border-slate-100 transition-colors">
                                <div className="text-right">
                                    <div className="text-lg font-semibold text-slate-800 leading-none">{licenseData.daysRemaining}</div>
                                    <div className="text-[9px] font-medium text-slate-400 uppercase tracking-widest mt-0.5">Days Left</div>
                                </div>
                                <div className="text-slate-300">
                                    <Clock size={16} />
                                </div>
                            </div>
                        </div>

                        {/* Details Grid - Slimmed Down */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div>
                                <div className="flex items-center gap-2 text-slate-400 text-[10px] font-medium uppercase tracking-widest mb-1">
                                    Valid From
                                </div>
                                <p className="text-slate-600 font-medium text-[13px]">{licenseData.validFrom}</p>
                            </div>
                            <div>
                                <div className="flex items-center gap-2 text-slate-400 text-[10px] font-medium uppercase tracking-widest mb-1">
                                    Valid Until
                                </div>
                                <p className="text-slate-600 font-medium text-[13px]">{licenseData.validTo}</p>
                            </div>
                            <div>
                                <div className="flex items-center gap-2 text-slate-400 text-[10px] font-medium uppercase tracking-widest mb-1">
                                    License ID
                                </div>
                                <code className="text-slate-500 font-mono text-[11px] font-medium">
                                    {licenseData.licenseKey}
                                </code>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Minimalist Info Box */}
                <div className="mt-6 bg-slate-50/50 border border-slate-100 p-4 rounded-2xl flex items-start gap-3">
                    <div className="text-amber-500 mt-0.5 shrink-0">
                        <AlertCircle size={16} />
                    </div>
                    <div>
                        <h4 className="text-[12px] font-semibold text-slate-700 mb-0.5">Subscription Management</h4>
                        <p className="text-[11px] text-slate-500 leading-relaxed font-normal">
                            Reminders are sent 30 days prior to expiration. For upgrades, please contact your system administrator.
                        </p>
                    </div>
                </div>
            </ConfigSection>

        </div>
    );
}
